import { type DateSubconditionState } from "@redotech/redo-model/return-flow/condition-validate";
import { DateSubconditionType } from "@redotech/redo-model/return-flow/date-subcondition";

export {
  emptyDateSubconditionState,
  fromModelDateSubcondition,
  validateDateSubcondition,
  type DateSubconditionState,
} from "@redotech/redo-model/return-flow/condition-validate";

export const toModelDateSubcondition = (state: DateSubconditionState) => {
  const result: any = { type: state.type };

  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        result.beforeDate = state.beforeDate.toISOString();
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        result.afterDate = state.afterDate.toISOString();
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate) {
        result.startDate = state.startDate.toISOString();
      }
      if (state.endDate) {
        result.endDate = state.endDate.toISOString();
      }
      break;
    case DateSubconditionType.LAST_N_DAYS:
      result.days = state.days;
      break;
  }

  return result;
};

// Helper function to provide human-readable standalone description
export const getDateSubconditionDescription = (
  state: DateSubconditionState,
): string => {
  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      return state.beforeDate
        ? `On or before ${state.beforeDate.toLocaleDateString()}`
        : "On or before date";
    case DateSubconditionType.ON_OR_AFTER:
      return state.afterDate
        ? `On or after ${state.afterDate.toLocaleDateString()}`
        : "On or after date";
    case DateSubconditionType.BETWEEN:
      return state.startDate && state.endDate
        ? `Between ${state.startDate.toLocaleDateString()} and ${state.endDate.toLocaleDateString()}`
        : "Between dates";
    case DateSubconditionType.LAST_N_DAYS:
      return state.days ? `Last ${state.days} days` : "Last n days";
    default:
      return "All time";
  }
};

// Helper function to get date subcondition description suffix for conditions
export const getDateSubconditionSuffix = (
  state: DateSubconditionState,
): string => {
  // If it's all time, don't add anything
  if (state.type === DateSubconditionType.ALL_TIME) {
    return "";
  }

  // Otherwise, add the appropriate suffix based on the type
  switch (state.type) {
    case DateSubconditionType.LAST_N_DAYS:
      if (state.days) {
        return ` in the last ${state.days} days`;
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        return ` on or after ${state.afterDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        return ` on or before ${state.beforeDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate && state.endDate) {
        return ` between ${state.startDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })} and ${state.endDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
  }

  return "";
};
